/*
 * Gecombineerde keuze van verzendland en taal.
 *
 * Het domein volgt de TAAL, niet het land: Duits bestaat alleen op lundia.de,
 * elke andere taal op lundia.com. Een Duits bezorgland met een Engelse site
 * blijft dus gewoon op lundia.com.
 *
 * Het localization-formulier van Shopify wisselt alleen binnen het eigen
 * domein, dus een sprong tussen beide domeinen doen we zelf. Shopify lost
 * de vertaalde handle op de bestemming daarna met een 301 op, dus het pad
 * mag gewoon mee.
 */
/*
 * Sinds 2026-09-16 hangt lundia.de aan beide markten, dus elk bezorgland houdt
 * daar zijn eigen prijzen. Daarom staat elke taal open voor elk land: de taal
 * is van de lezer, het land van het pakket. De volgorde stuurt de keuze, niets
 * wordt verborgen.
 */
/* Alleen hier staat Duits vooringevuld; je kunt hem daarna gewoon wijzigen. */
const GERMAN_DEFAULT_COUNTRIES = ["DE", "AT"];

const LANGUAGE_PREFIX = { nl: "", en: "/en", fr: "/fr" };

/*
 * De taal die bij een bezorgland hoort. Belgie en Luxemburg hebben er twee.
 * Dit bepaalt alleen wat bovenaan staat, niet wat je mag kiezen: de taal is van
 * de lezer en het land van het pakket, en dat hoeft niet hetzelfde te zijn. Een
 * Nederlander die naar Duitsland stuurt houdt dus gewoon zijn Nederlands.
 */
const COUNTRY_LANGUAGES = {
  NL: ["nl"],
  BE: ["nl", "fr"],
  FR: ["fr"],
  LU: ["fr", "de"],
  DE: ["de"],
  AT: ["de"],
};

/* Vaste volgorde voor alles wat niet bij het land of de lezer hoort. */
const LANGUAGE_ORDER = ["nl", "en", "fr", "de"];

class MarketLanguageModal extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector(".market-language-modal__dialog");
    this.form = this.querySelector("form");
    this.countrySelect = this.querySelector("[data-market-language-country]");
    this.languageSelect = this.querySelector("[data-market-language-language]");

    this.basePath = this.dataset.basePath || "/";
    this.comOrigin = this.dataset.comOrigin;
    this.deOrigin = this.dataset.deOrigin;
    /*
     * Het domein uit de browser lezen in plaats van uit Liquid: request.hostname
     * bleek op lundia.de niet 'lundia.de' terug te geven, waardoor de sprong
     * terug naar lundia.com stilletjes uitbleef. De Liquid-waarde blijft als
     * terugval voor het geval er geen window is.
     */
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    this.onDeDomain = host
      ? host === new URL(this.deOrigin).hostname
      : this.dataset.onDeDomain === "true";
    this.currentCountry = this.dataset.currentCountry;
    this.currentLanguage = this.dataset.currentLanguage;

    const i18nTag = this.querySelector("[data-market-language-i18n]");
    this.i18n = i18nTag ? JSON.parse(i18nTag.textContent) : {};

    this.lastFocused = null;
    this.onKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.querySelectorAll("[data-market-language-dismiss]").forEach((el) =>
      el.addEventListener("click", () => this.close())
    );
    this.countrySelect.addEventListener("change", () => {
      this.syncLanguage();
      this.sortLanguages();
      this.translate();
    });
    this.languageSelect.addEventListener("change", () => this.translate());
    this.form.addEventListener("submit", (event) => this.handleSubmit(event));

    document.addEventListener("click", (event) => {
      const trigger = event.target.closest("[data-market-language-open]");
      if (!trigger) return;
      event.preventDefault();
      this.open(trigger);
    });

    this.syncLanguage();
    this.sortLanguages();
    this.translate();
  }

  /*
   * Zet de taal van het bezorgland bovenaan, daaronder de taal die je nu leest,
   * en daarna de rest. Er verdwijnt niets: dit stuurt alleen wat je als eerste
   * ziet, zodat een Duitse klant niet zomaar in de Nederlandse site belandt met
   * acties die niet voor hem gelden.
   */
  sortLanguages() {
    const selected = this.languageSelect.value;
    const order = [...(COUNTRY_LANGUAGES[this.countrySelect.value] || [])];

    if (!order.includes(selected)) order.push(selected);
    LANGUAGE_ORDER.forEach((code) => {
      if (!order.includes(code)) order.push(code);
    });

    order.forEach((code) => {
      const option = this.languageSelect.querySelector('option[value="' + code + '"]');
      /* appendChild verplaatst een bestaande optie naar achteren. */
      if (option) this.languageSelect.appendChild(option);
    });

    /* Het verplaatsen van de gekozen optie kan de selectie wissen. */
    this.languageSelect.value = selected;
  }

  /*
   * De modal wisselt van taal zodra je de taal-select verandert, dus nog
   * voordat je opslaat. Daarom staan alle vier de talen in de pagina.
   */
  translate() {
    const lang = this.languageSelect.value;
    const strings = this.i18n[lang];
    if (!strings) return;

    this.querySelectorAll("[data-i18n]").forEach((el) => {
      const value = strings[el.dataset.i18n];
      if (value) el.textContent = value;
    });

    this.querySelectorAll("[data-i18n-aria]").forEach((el) => {
      const value = strings[el.dataset.i18nAria];
      if (value) el.setAttribute("aria-label", value);
    });

    const suffix = lang.charAt(0).toUpperCase() + lang.slice(1);

    /* De landnamen in de select volgen dezelfde taal. */
    Array.from(this.countrySelect.options).forEach((option) => {
      const name = option.dataset["name" + suffix];
      if (name) option.textContent = name + " (" + option.dataset.currency + ")";
    });
  }

  /*
   * Bij DE en AT springt de taal vanzelf op Duits, maar je mag hem daarna
   * gewoon terugzetten naar Nederlands, Engels of Frans; je blijft dan op
   * lundia.com. Bij FR en LU blijft Duits een keuze die je zelf maakt.
   */
  syncLanguage() {
    /* Alles blijft kiesbaar; alleen DE en AT krijgen Duits vooringevuld. */
    Array.from(this.languageSelect.options).forEach((option) => {
      option.hidden = false;
      option.disabled = false;
    });

    if (GERMAN_DEFAULT_COUNTRIES.includes(this.countrySelect.value)) {
      this.languageSelect.value = "de";
    }
  }

  handleSubmit(event) {
    const country = this.countrySelect.value;
    const language = this.languageSelect.value;
    const wantsDeDomain = language === "de";

    /*
     * Binnen hetzelfde domein doet het Shopify-formulier het werk: het zet
     * land en taal in een keer en stuurt terug naar de vertaalde URL.
     */
    if (wantsDeDomain === this.onDeDomain) return;

    event.preventDefault();

    /*
     * Het gekozen land moet mee over de domeingrens. Een cookie op lundia.com
     * geldt niet op lundia.de, dus zonder deze parameter valt de bezoeker daar
     * terug op het standaardland van dat domein (Oostenrijk) en raakt zijn
     * keuze stil verloren. Shopify leest ?country= en onthoudt hem.
     */
    const prefix = wantsDeDomain ? "" : LANGUAGE_PREFIX[language] || "";
    const target = new URL(
      (wantsDeDomain ? this.deOrigin : this.comOrigin) + prefix + this.basePath
    );
    target.searchParams.set("country", country);
    window.location.href = target.toString();
  }

  open(trigger) {
    this.lastFocused = trigger || document.activeElement;
    this.hidden = false;

    /*
     * De scrollbar verdwijnt zodra we de body op overflow:hidden zetten, en de
     * pagina schuift dan die breedte naar rechts. Compenseren met evenveel
     * padding houdt alles op zijn plek. De header is sticky en staat dus in de
     * normale flow, die schuift vanzelf mee.
     */
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty(
      "--market-language-scrollbar",
      scrollbar + "px"
    );
    document.body.classList.add("market-language-modal-open");
    document.addEventListener("keydown", this.onKeydown);

    /* Reflow forceren in plaats van requestAnimationFrame: rAF vuurt niet in
       een tab die op de achtergrond staat, waardoor de modal wel opent maar
       onzichtbaar blijft. */
    void this.offsetWidth;
    this.classList.add("is-open");
    this.countrySelect.focus();
  }

  close() {
    this.classList.remove("is-open");
    this.hidden = true;
    document.body.classList.remove("market-language-modal-open");
    document.documentElement.style.removeProperty("--market-language-scrollbar");
    document.removeEventListener("keydown", this.onKeydown);
    if (this.lastFocused) this.lastFocused.focus();
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close();
      return;
    }
    if (event.key !== "Tab") return;

    const focusable = this.dialog.querySelectorAll(
      'button, select, a[href], input, [tabindex]:not([tabindex="-1"])'
    );
    const items = Array.from(focusable).filter((el) => !el.disabled && !el.hidden);
    if (items.length === 0) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

customElements.define("market-language-modal", MarketLanguageModal);
